import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MotiveRadar from "@/components/MotiveRadar";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { getMotiveIcon, scoreBand } from "@/lib/motives";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  ShieldQuestion,
  Trophy,
  Flame,
  HeartHandshake,
  UserMinus,
  Sparkles,
  AlertTriangle,
  BadgeCheck,
  Download,
} from "lucide-react";

const ROLE_META = [
  {
    key: "kapitaen_kandidaten",
    title: "Kapitän-Kandidaten",
    icon: Trophy,
    hint: "Höchste Werte in Führung + Disziplin + Team.",
    color: "#CCFF00",
  },
  {
    key: "motivatoren",
    title: "Motivatoren",
    icon: Flame,
    hint: "Wer treibt das Team an und spricht Klartext.",
    color: "#00F0FF",
  },
  {
    key: "druck_spieler",
    title: "Druck-Spieler",
    icon: BadgeCheck,
    hint: "Kann in engen Momenten Verantwortung übernehmen.",
    color: "#CCFF00",
  },
  {
    key: "braucht_viel_lob",
    title: "Braucht viel Lob",
    icon: HeartHandshake,
    hint: "Braucht Anerkennung und Bestätigung.",
    color: "#F59E0B",
  },
  {
    key: "kritik_empfindlich",
    title: "Kritik-empfindlich",
    icon: AlertTriangle,
    hint: "Kritik konstruktiv & unter vier Augen.",
    color: "#EC4899",
  },
  {
    key: "mitlaeufer",
    title: "Eher Mitläufer",
    icon: UserMinus,
    hint: "Kann zusätzlich zu Verantwortung geführt werden.",
    color: "#94A3B8",
  },
];

export default function TeamAnalysis() {
  const { teamId } = useParams();
  const [data, setData] = useState(null);
  const [motives, setMotives] = useState([]);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, m] = await Promise.all([
          api.get(`/teams/${teamId}/analysis`),
          api.get("/meta/motives"),
        ]);
        setData(r.data);
        setMotives(m.data.motives);
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [teamId]);

  const downloadTeamPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/teams/${teamId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `FMP_Team_${(data?.team?.name || "Team").replace(/ /g, "_")}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Team-PDF heruntergeladen");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  if (err)
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center text-red-400">
        {err}
      </div>
    );
  if (!data)
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center text-white/40 font-display tracking-widest">
        Analyse wird geladen …
      </div>
    );

  const { team, players, analysis } = data;

  if (analysis.player_count === 0) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-16 text-center">
        <Users className="w-10 h-10 text-white/30 mx-auto mb-4" />
        <div className="font-heading text-2xl mb-2">
          Noch keine abgeschlossenen Tests
        </div>
        <p className="text-white/60 mb-6">
          Sobald mindestens ein Spieler den Test abgeschlossen hat, erscheint hier die Team-Analyse.
        </p>
        <Link to={`/teams/${teamId}`} className="btn-primary">
          Zurück zum Team
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-12 space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link
          to={`/teams/${teamId}`}
          data-testid="team-analysis-back-btn"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#CCFF00] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zum Team
        </Link>
        <div className="flex flex-wrap gap-3">
          <a
            data-testid="team-analysis-csv-btn"
            href={`${API_BASE}/teams/${teamId}/csv`}
            className="btn-ghost"
            onClick={(e) => {
              // ensure auth cookie is sent
              const token = localStorage.getItem("fmp_token");
              if (token) {
                e.preventDefault();
                fetch(`${API_BASE}/teams/${teamId}/csv`, {
                  headers: { Authorization: `Bearer ${token}` },
                  credentials: "include",
                })
                  .then((r) => r.blob())
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `FMP_Team_${(data?.team?.name || "Team").replace(/ /g, "_")}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("CSV heruntergeladen");
                  })
                  .catch(() => toast.error("Download fehlgeschlagen"));
              }
            }}
          >
            <Download className="w-5 h-5" />
            CSV
          </a>
          <button
            data-testid="team-analysis-pdf-btn"
            onClick={downloadTeamPdf}
            disabled={downloading}
            className="btn-primary"
          >
            <Download className="w-5 h-5" />
            {downloading ? "PDF wird erstellt …" : "Team-PDF herunterladen"}
          </button>
        </div>
      </div>

      <div>
        <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
          / TEAM-ANALYSE
        </div>
        <h1
          data-testid="team-analysis-heading"
          className="font-heading font-black text-4xl md:text-5xl tracking-tight mb-2"
        >
          {team.name}
        </h1>
        <div className="text-white/60">
          {analysis.player_count} Spieler getestet · Aggregiertes Team-Profil
        </div>
      </div>

      {/* Team radar */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-elevated p-6 md:p-8">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
            / TEAM-MOTIVPROFIL
          </div>
          <h3 className="font-heading font-black text-2xl mb-4">Durchschnitt</h3>
          <MotiveRadar scores={analysis.avg_scores} motives={motives} />
        </div>

        <div className="space-y-4">
          <div className="card-elevated p-6 md:p-8">
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
              / TEAM-STÄRKEN
            </div>
            <div className="space-y-2">
              {analysis.strengths.map((m) => {
                const Icon = getMotiveIcon(m.icon);
                return <TeamMotiveRow key={m.slug} m={m} Icon={Icon} />;
              })}
            </div>
          </div>
          <div className="card-elevated p-6 md:p-8">
            <div className="font-display text-xs tracking-[0.3em] text-white/40 mb-3">
              / TEAM-LÜCKEN
            </div>
            <div className="space-y-2">
              {analysis.gaps.map((m) => {
                const Icon = getMotiveIcon(m.icon);
                return <TeamMotiveRow key={m.slug} m={m} Icon={Icon} muted />;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Rollen im Team */}
      <div>
        <div className="mb-4">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-2">
            / ROLLEN IM TEAM
          </div>
          <h3 className="font-heading font-black text-2xl md:text-3xl">
            Wer übernimmt was?
          </h3>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ROLE_META.map((role) => {
            const list = analysis.roles[role.key] || [];
            const Icon = role.icon;
            return (
              <div
                key={role.key}
                data-testid={`role-card-${role.key}`}
                className="card-elevated p-6"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-5 h-5" style={{ color: role.color }} />
                  <div
                    className="font-display text-xs tracking-[0.2em]"
                    style={{ color: role.color }}
                  >
                    {role.title.toUpperCase()}
                  </div>
                </div>
                <div className="text-xs text-white/50 mb-3">{role.hint}</div>
                <div className="space-y-1">
                  {list.length > 0 ? (
                    list.map((p, i) => (
                      <Link
                        key={p.id}
                        to={p.id ? `/players/${p.id}` : "#"}
                        className="flex items-center justify-between p-2 rounded hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-display text-xs text-white/40 w-4">
                            {i + 1}
                          </span>
                          <span className="font-heading font-bold text-sm">{p.name}</span>
                        </div>
                        <div className="font-display text-xs text-white/60">
                          {p.score}%
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="text-xs text-white/40 italic">
                      Keine Daten
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Missing archetypes */}
      {analysis.missing_archetypes?.length > 0 && (
        <div className="card-elevated p-6 md:p-8">
          <div className="font-display text-xs tracking-[0.3em] text-white/40 mb-3">
            / FEHLENDE SPIELERTYPEN
          </div>
          <h3 className="font-heading font-black text-2xl mb-4">
            Diese Typen fehlen deinem Team
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {analysis.missing_archetypes.map((a) => (
              <div
                key={a.slug}
                className="p-4 border border-white/10 rounded-md bg-[#0f1013]"
              >
                <div className="font-heading font-bold text-lg mb-1">
                  {a.name}
                </div>
                <div className="text-xs text-white/50 italic">{a.tagline}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Player list */}
      <div className="card-elevated p-6 md:p-8">
        <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
          / GETESTETE SPIELER
        </div>
        <h3 className="font-heading font-black text-2xl mb-6">
          {players.length} Spieler
        </h3>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {players.map((p) => (
            <Link
              key={p.player_id}
              to={`/players/${p.player_id}`}
              data-testid={`analysis-player-${p.player_id}`}
              className="p-4 border border-white/10 rounded-md hover:border-[#CCFF00]/40 bg-[#0f1013] transition-colors block"
            >
              <div className="font-heading font-bold mb-1">{p.name}</div>
              <div className="text-xs text-white/50 mb-2">{p.position}</div>
              <div
                className="font-display text-xs tracking-widest"
                style={{ color: "#CCFF00" }}
              >
                {p.archetype.name}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function TeamMotiveRow({ m, Icon, muted }) {
  const band = scoreBand(m.score);
  return (
    <div className="flex items-center gap-3 p-2">
      <div
        className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0"
        style={{
          background: muted ? "rgba(255,255,255,0.05)" : `${m.color}22`,
          border: `1px solid ${muted ? "rgba(255,255,255,0.1)" : m.color + "66"}`,
          color: muted ? "#94A3B8" : m.color,
        }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-sm">{m.name}</div>
        <div
          className="font-display text-[10px] tracking-widest"
          style={{ color: band.color }}
        >
          {band.label}
        </div>
      </div>
      <div className="font-heading font-black text-xl">{m.score}</div>
    </div>
  );
}
